import { Icon } from "@iconify/react";
import React, { useEffect, useRef, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import { useFormik, FormikProvider, ErrorMessage, FieldArray } from "formik";
import * as yup from "yup";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import { usePostBusinessDetailMutation } from "../../../../rtk-query/slices/onboarding/businessDetailSlice";
import {
  useGetCitiesQuery,
  useGetCountriesQuery,
} from "../../../../rtk-query/slices/select-options/selectOptionsSlice";
import {
  formatCities,
  formatCountries,
} from "../../../../helpers/selectOptionFormatter";
import { useNavigate } from "react-router-dom";
import CropperModal from "../../../../modals/cropperModal/CropperModal";
import { useGetUserProfileQuery } from "../../../../rtk-query/slices/profile/profileSlice";

function BusinessDetailsTabContent({ setCurrentTab }) {
  const navigate = useNavigate();
  const { data: cityData } = useGetCitiesQuery();
  const { data: countryData } = useGetCountriesQuery();
  const { data: userProfile } = useGetUserProfileQuery();
  const [showModal, setShowModal] = useState(false);
  const [cropper, setCropper] = useState(null);
  const imageRef = useRef(null);
  // const [country, setCountry] = useState("");
  const [logo, setLogo] = useState(null);
  const [logoError, setLogoError] = useState("");
  const handleLogoChange = (e) => {
    setLogoError("");
    if (e.target.files && e.target.files[0]) {
      setLogo(e.target.files[0]);
      setShowModal(true);
    }
  };

  const handleLogoDelete = () => {
    setLogo(null);
    setCropper(null);
  };

  const cities = cityData ? formatCities(cityData?.data) : [];
  const countries = countryData ? formatCountries(countryData?.data) : [];

  const validationSchema = yup.object({
    email_address: yup
      .array()
      .of(yup.string().email("Invalid email address"))
      .required("At least one email address is required"),
    experienceLocation: yup
      .object()
      .required("Experience location is required"),
    aboutYourself: yup.string().min(100, "Minimum 100 characters"),
    publicName: yup.string().required("Public name is required"),
    legalName: yup.string().required("Legal name is required"),
    registrationCountry: yup
      .object()
      .required("Registration country is required"),

    phoneNumber: yup.string().required("Phone number is required"),
    emergencyNumber: yup.string().required("Emergency number is required"),
    website: yup
      .string()
      .matches(
        /^(https?:\/\/)?([\w\d-]+\.)+[\w\d]{2,}(:\d+)?(\/.*)?$/,
        "Invalid website URL"
      ),
  });

  const [postBusinessDetail, { isLoading }] = usePostBusinessDetailMutation();

  const [initialValues, setInitialValues] = useState({
    email_address: [""],
    experienceLocation: null,
    aboutYourself: "",
    publicName: "",
    legalName: "",
    registrationCountry: null,
    emergencyNumber: "",
    phoneNumber: "",
    website: "",
  });

  useEffect(() => {
    if (userProfile?.data) {
      setLogo(userProfile?.data?.supplier?.supplier_company_logo || null);
      setInitialValues({
        email_address:
          userProfile?.data?.supplier?.emails?.length > 0
            ? userProfile?.data?.supplier?.emails?.map(
                (email) => email?.email_address
              )
            : [""],
        experienceLocation: userProfile?.data?.supplier?.experience_city
          ? {
              label: userProfile?.data?.supplier?.experience_city?.city_name,
              value: userProfile?.data?.supplier?.experience_city?.city_id,
            }
          : null,
        aboutYourself: userProfile?.data?.supplier?.company_overview || "",
        publicName: userProfile?.data?.supplier?.supplier_public_name,
        legalName: userProfile?.data?.supplier?.supplier_legal_name,
        registrationCountry: userProfile?.data?.supplier?.registered_country
          ? {
              label: userProfile?.data?.supplier?.registered_country?.name,
              value:
                userProfile?.data?.supplier?.registered_country?.country_id,
            }
          : null,

        phoneNumber: userProfile?.data?.supplier?.supplier_phone_number || "",
        emergencyNumber:
          userProfile?.data?.supplier?.supplier_emergency_number || "",
        website: userProfile?.data?.supplier?.supplier_website || "",
      });
      setLogoError("");
    }
  }, [userProfile]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true, // This ensures the form reinitializes with new values
    onSubmit: async (values, { resetForm }) => {
      try {
        if (!logo) {
          setLogoError("Company logo is required");
          return;
        }
        setLogoError("");
        const formData = new FormData();
        values?.email_address?.map((email) => {
          if (email) {
            formData.append("email_address[]", email);
          }
        });
        formData.append("experience_city", values.experienceLocation.value);
        if (values?.aboutYourself) {
          formData.append("company_overview", values.aboutYourself);
        } else {
          formData.append("company_overview", "");
        }
        formData.append("public_name", values.publicName);
        formData.append("legal_name", values.legalName);
        formData.append(
          "registration_country",
          values.registrationCountry.value
        );

        formData.append("phone_number", values.phoneNumber);
        formData.append("emergency_number", values.emergencyNumber);
        formData.append("supplier_website", values.website);
        if (logo && typeof logo !== "string") {
          formData.append("company_logo", logo);
        }

        const response = await postBusinessDetail(formData);
        if (response?.data?.status) {
          toast.success(response?.data?.message, {
            position: "top-center",
            autoClose: 3000,
          });
          setCurrentTab(3);
        }
        if (response?.error) {
          const errors = response?.error?.data?.errors;
          if (response?.error?.status == 401) {
            localStorage.removeItem("DODLoginToken");
            toast.error(response?.error?.data?.message, {
              position: "top-center",
              autoClose: 3000,
            });
            navigate("/login");
          } else if (errors) {
            for (const field in errors) {
              if (errors.hasOwnProperty(field)) {
                const combinedErrors = errors[field].join(" ");
                // if (field === "supplier_type") {
                //   setFieldError("option", combinedErrors);
                // }
              }
            }
            toast.error(response?.error?.data?.message, {
              position: "top-center",
              autoClose: 3000,
            });
          } else {
            toast.error(response?.error?.data?.message, {
              position: "top-center",
              autoClose: 3000,
            });
          }
        }
      } catch (error) {
        toast.error("An unexpected error occurred. Please try again.");
      } finally {
      }
    },
  });

  // ... your existing handleImageUpload function

  useEffect(() => {
    // Initialize Cropper when the modal opens and an image is selected
    if (showModal && logo && imageRef.current) {
      if (typeof logo !== "string") {
        const image = imageRef.current;
        image.src = typeof logo === "string" ? logo : URL.createObjectURL(logo);
        setCropper(
          new Cropper(image, {
            viewMode: 1, // Allows the image to stay within the canvas while allowing free cropping
            zoomable: true, // Users can zoom in/out
            background: false, // Removes the dark background
            responsive: true, // Ensures responsiveness
            autoCropArea: 1, // Starts with the full image selected
            checkOrientation: false, // Prevents rotation issues
            guides: true, // Displays guides to help cropping
            movable: true, // Allows moving the image within the cropper
            cropBoxMovable: true, // Allows moving the crop box
            cropBoxResizable: true, // Allows resizing the crop box dynamically
          })
        );
      }
    }

    // Cleanup Cropper when the modal closes or component unmounts
    return () => {
      if (cropper) {
        cropper.destroy();
      }
    };
  }, [showModal, logo, imageRef, setCropper]);

  return (
    <div className="ob-pi-form-container">
      <h3 className="ob-pi-form-heading m-0">Let's start with the basics</h3>
      <p className="ob-pi-form-description m-0">
        Primary details explaining your business
      </p>
      <FormikProvider value={formik}>
        <Form className="mt-4" onSubmit={formik.handleSubmit}>
          {/** your experience location */}
          <Form.Group controlId="experienceLocation">
            <p className="ob-pi-label m-0">
              Where does your experience take place?
              <span className="text-danger ps-1">*</span>
            </p>
            <p className="ob-pi-note pb-1 m-0">
              If you offer multiple experiences, provide the location where most
              of them take place.
            </p>
            <Select
              name="experienceLocation"
              options={cities}
              placeholder="Search city or area"
              className="ob-pi-input mt-1 p-0 form-control"
              value={formik.values.experienceLocation}
              onChange={(e) => {
                formik.setFieldValue("experienceLocation", e);
              }}
              components={{
                Control: ({ children, ...props }) => {
                  return (
                    <components.Control {...props}>
                      {children}
                      <div className="select-icon">
                        <Icon icon="ic:baseline-keyboard-arrow-down" />
                      </div>
                    </components.Control>
                  );
                },
              }}
            />
            <ErrorMessage
              name="experienceLocation"
              component="div"
              className="text-danger mb-2"
            />
          </Form.Group>

          {/** yourself */}
          <Form.Group controlId="aboutYourself" className="mt-4">
            <p className="ob-pi-label">
              Tell us about yourself <span>(optional)</span>
            </p>
            <p className="ob-pi-note pb-1 m-0">
              Write a brief description about the tours, activities, or other
              experiences you offer.
            </p>
            <Form.Control
              as="textarea"
              placeholder="Write a brief description about the tours, activities, or other experiences you offer."
              className="ob-pi-textarea mt-1"
              name="aboutYourself"
              value={formik.values.aboutYourself}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <ErrorMessage
              name="aboutYourself"
              component="div"
              className="text-danger mb-2"
            />
          </Form.Group>

          {/** email address */}

          {/* Multiple Email Section */}
          <FieldArray
            name="email_address"
            render={(arrayHelpers) => (
              <div className="position-relative">
                <p className="ob-pi-label mt-4">
                  Email Address<span className="text-danger ps-1">*</span>
                </p>
                <p className="ob-pi-note pb-1 m-0">
                  This is the email used when marketing your tours, activities,
                  or experiences.
                </p>
                {formik.values.email_address.map((email, index) => (
                  <div key={index} className="d-flex align-items-center mb-2">
                    <Form.Control
                      type="email"
                      placeholder="Enter email address"
                      name={`email_address.${index}`}
                      value={email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`ob-pi-input ${
                        formik.touched.email_address &&
                        formik.errors.email_address
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    {formik.values.email_address.length > 1 && index !== 0 && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-2"
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        <Icon
                          icon="ic:baseline-delete"
                          fontSize={22}
                          className="text-danger"
                        />
                      </button>
                    )}
                    {index == 0 && (
                      <button
                        type="button"
                        className="btn btn-link p-0 ms-2"
                        onClick={() => arrayHelpers.push("")}
                      >
                        <Icon
                          fontSize={22}
                          icon="ic:baseline-add-circle"
                          color="#eb662b"
                        />
                      </button>
                    )}
                  </div>
                ))}
                {formik.errors.email_address && (
                  <div className="text-danger">
                    {formik.errors.email_address}
                  </div>
                )}
              </div>
            )}
          />

          <Form.Group controlId="publicName" className="mt-4">
            <p className="ob-pi-label">
              Public name<span className="text-danger ps-1">*</span>
            </p>
            <p className="ob-pi-note pb-1 m-0">
              This is the name used when marketing your tours, activities, or
              experiences.
            </p>
            <Form.Control
              type="text"
              placeholder="Public name"
              className="ob-pi-input mt-1"
              name="publicName"
              value={formik.values.publicName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <ErrorMessage
              name="publicName"
              component="div"
              className="text-danger mb-2"
            />
          </Form.Group>

          <Form.Group controlId="legalName" className="mt-4">
            <p className="ob-pi-label">
              Legal name<span className="text-danger ps-1">*</span>
            </p>
            <p className="ob-pi-note pb-1 m-0">
              For registered businesses, this is the name that appears on
              official documents and legal papers.
            </p>
            <Form.Control
              type="text"
              placeholder="Legal name"
              className="ob-pi-input mt-1"
              name="legalName"
              value={formik.values.legalName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <ErrorMessage
              name="legalName"
              component="div"
              className="text-danger mb-2"
            />
          </Form.Group>

          <Form.Group controlId="registrationCountry" className="mt-4">
            <p className="ob-pi-label">
              Registration country<span className="text-danger ps-1">*</span>
            </p>
            <p className="ob-pi-note pb-1 m-0">
              Specify where your business origin is
            </p>
            <Select
              name="registrationCountry"
              options={countries}
              placeholder="Select country"
              className="ob-pi-input mt-1 p-0 form-control"
              value={formik.values.registrationCountry}
              onChange={(e) => {
                formik.setFieldValue("registrationCountry", e);
              }}
              components={{
                Control: ({ children, ...props }) => {
                  return (
                    <components.Control {...props}>
                      {children}
                      <div className="select-icon">
                        <Icon icon="ic:baseline-keyboard-arrow-down" />
                      </div>
                    </components.Control>
                  );
                },
              }}
            />

            <ErrorMessage
              name="registrationCountry"
              component="div"
              className="text-danger mb-2"
            />
          </Form.Group>

          <Form.Group controlId="phoneNumber" className="mt-4">
            <p className="ob-pi-label">
              Phone number<span className="text-danger ps-1">*</span>
            </p>
            <p className="ob-pi-note pb-1 m-0">
              Travelers will be able to call this number if they have questions.
            </p>
            <PhoneInput
              country={"ae"}
              placeholder="Enter phone number"
              inputClass="ob-pi-input w-100 mt-1"
              name="phoneNumber"
              value={formik.values.phoneNumber}
              onChange={(e) => {
                formik.setFieldValue("phoneNumber", e);
              }}
            />
            <ErrorMessage
              name="phoneNumber"
              component="div"
              className="text-danger mb-2"
            />
          </Form.Group>

          <Form.Group controlId="emergencyNumber" className="mt-4">
            <p className="ob-pi-label">
              Emergency number<span className="text-danger ps-1">*</span>
            </p>
            <p className="ob-pi-note pb-1 m-0">
              Travelers will be able to call this number if they have questions.
            </p>
            <PhoneInput
              country={"ae"}
              placeholder="Enter emergency number"
              inputClass="ob-pi-input w-100 mt-1"
              name="emergencyNumber"
              value={formik.values.emergencyNumber}
              onChange={(e) => {
                formik.setFieldValue("emergencyNumber", e);
              }}
            />
            <ErrorMessage
              name="emergencyNumber"
              component="div"
              className="text-danger mb-2"
            />
          </Form.Group>

          <Form.Group controlId="website" className="mt-4">
            <p className="ob-pi-label">Website</p>
            <p className="ob-pi-note pb-1 m-0">
              Provide official URL of your website or other link show case all
              details about your business
            </p>
            <Form.Control
              type="text"
              placeholder="https://website.com"
              className="ob-pi-input mt-1"
              name="website"
              value={formik.values.website}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            <ErrorMessage
              name="website"
              component="div"
              className="text-danger mb-2"
            />
          </Form.Group>

          <Form.Group controlId="companyLogo" className="mt-4">
            <p className="ob-pi-label">
              Add company logo or trademark
              <span className="text-danger ps-1">*</span>
            </p>
            <p className="ob-pi-note pb-1 m-0">
              This is to be shown to customers during bookings.
            </p>
            {!logo ? (
              <div
                className="ob-pi-logo-upload mt-1"
                onClick={() => document.getElementById("logoUpload").click()}
              >
                <div className="ob-pi-logo-add-icon">
                  <Icon icon="akar-icons:plus" fontSize={20} />
                </div>
                <span className="ob-pi-logo-upload-text">
                  {" "}
                  Add company logo
                </span>
              </div>
            ) : (
              <div className="ob-pi-logo-preview mt-1 position-relative">
                <img
                  width={100}
                  height={100}
                  src={
                    logo && typeof logo === "string"
                      ? logo
                      : typeof logo !== "string"
                      ? URL.createObjectURL(logo)
                      : "/path/to/default/image.png" // Optional: fallback image if logo is null or undefined
                  }
                  alt="Company Logo"
                  className="ob-pi-logo-image"
                />
                <Icon
                  icon="akar-icons:cross"
                  className="ob-pi-logo-delete-icon position-absolute"
                  onClick={handleLogoDelete}
                />
              </div>
            )}
            <input
              type="file"
              id="logoUpload"
              accept="image/*"
              onChange={handleLogoChange}
              style={{ display: "none" }}
            />
            {logoError && <div className="text-danger ">{logoError}</div>}
          </Form.Group>

          <div className="d-flex justify-content-between mt-5">
            <button className="back-btn">Back</button>
            <button type="submit" className="submit-btn" disabled={isLoading}>
              {!isLoading ? (
                "Save & Continue"
              ) : (
                <>
                  Save & Continue <Spinner size="sm" />
                </>
              )}
            </button>
          </div>
        </Form>
      </FormikProvider>
      <CropperModal
        showModal={showModal}
        handleModalClose={() => setShowModal(false)}
        imageRef={imageRef}
        cropper={cropper}
        setLogo={setLogo}
      />
    </div>
  );
}

export default BusinessDetailsTabContent;
